import { useState, useEffect, useRef } from 'react';
import { Trash2, Search, X, MapPin, Lock, Unlock, ChevronDown, ChevronUp, Download, Copy } from 'lucide-react';
import { NotesService } from '@/lib/notesService';
import { CategoriesService } from '@/lib/categoriesService';
import { MediaService } from '@/lib/mediaService';
import { DecryptedNote, DecryptedCategory } from '@/lib/types';
import { useToast } from '@/contexts/ToastContext';
import { EncryptedImage, EncryptedVideo } from '@/components/EncryptedMedia';
import { FileAttachment } from '@/components/FileAttachment';
import './Notes.css';

interface GroupedNotes {
  [key: string]: DecryptedNote[];
}

export default function Notes() {
  const [notes, setNotes] = useState<DecryptedNote[]>([]);
  const [categories, setCategories] = useState<DecryptedCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [mapNote, setMapNote] = useState<DecryptedNote | null>(null);
  const [viewNote, setViewNote] = useState<DecryptedNote | null>(null);
  const [protectNote, setProtectNote] = useState<DecryptedNote | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
  const [verifyPasswordNote, setVerifyPasswordNote] = useState<DecryptedNote | null>(null);
  const [unprotectNote, setUnprotectNote] = useState<DecryptedNote | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editCategoryId, setEditCategoryId] = useState<string | null | undefined>(undefined);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const toast = useToast();

  useEffect(() => {
    loadNotes(true);
    loadCategories();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreNotes();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current = observer;

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loading, page]);

  async function loadNotes(reset = false) {
    if (reset) {
      setLoading(true);
      setPage(0);
      setHasMore(true);
    }

    const NOTES_PER_PAGE = 50;
    const offset = reset ? 0 : page * NOTES_PER_PAGE;
    const fetchedNotes = await NotesService.getNotes(NOTES_PER_PAGE, offset);

    if (reset) {
      setNotes(fetchedNotes);
      setExpandedGroups(new Set(Object.keys(groupNotesByWeek(fetchedNotes))));
    } else {
      setNotes(prev => [...prev, ...fetchedNotes]);
    }

    setHasMore(fetchedNotes.length === NOTES_PER_PAGE);
    setLoading(false);
  }

  async function loadMoreNotes() {
    if (!hasMore || loadingMore) return;

    setLoadingMore(true);
    const nextPage = page + 1;
    setPage(nextPage);

    const NOTES_PER_PAGE = 50;
    const offset = nextPage * NOTES_PER_PAGE;
    const fetchedNotes = await NotesService.getNotes(NOTES_PER_PAGE, offset);

    setNotes(prev => [...prev, ...fetchedNotes]);
    setHasMore(fetchedNotes.length === NOTES_PER_PAGE);
    setLoadingMore(false);
  }

  async function loadCategories() {
    const fetchedCategories = await CategoriesService.getCategories();
    setCategories(fetchedCategories);
  }

  async function handleDelete(noteId: string) {
    const success = await NotesService.deleteNote(noteId);
    setDeleteConfirm(null);

    if (success) {
      toast.success('Nota excluida com sucesso!');
      await loadNotes(true);
    } else {
      toast.error('Nao foi possivel excluir a nota');
    }
  }

  function toggleGroup(groupLabel: string) {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupLabel)) {
        newSet.delete(groupLabel);
      } else {
        newSet.add(groupLabel);
      }
      return newSet;
    });
  }

  async function handleDownload(url: string, filename: string) {
    try {
      toast.info('Iniciando download...');
      await MediaService.downloadEncryptedFile(url, filename);
      toast.success('Download concluído!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erro ao fazer download');
    }
  }

  function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  function handleStartEdit(note: DecryptedNote) {
    setIsEditingNote(true);
    setEditContent(note.content);
    setEditCategoryId(note.category_id || null);
  }

  function handleCancelEdit() {
    setIsEditingNote(false);
    setEditContent('');
    setEditCategoryId(undefined);
  }

  async function handleCopyContent(content: string) {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('Conteudo copiado!');
    } catch (error) {
      toast.error('Erro ao copiar conteudo');
    }
  }

  async function handleSaveEdit() {
    if (!viewNote) return;

    if (!editContent.trim()) {
      toast.error('Conteudo e obrigatorio');
      return;
    }

    try {
      const success = await NotesService.updateNote(viewNote.id, editContent.trim(), editCategoryId);

      if (!success) {
        toast.error('Erro ao atualizar nota');
        return;
      }

      const updatedCategory = editCategoryId
        ? categories.find(cat => cat.id === editCategoryId)
        : undefined;

      setNotes(prevNotes =>
        prevNotes.map(note =>
          note.id === viewNote.id
            ? {
                ...note,
                content: editContent.trim(),
                category_id: editCategoryId || undefined,
                category: updatedCategory ? {
                  ...updatedCategory,
                  name: updatedCategory.decryptedName,
                } : undefined
              }
            : note
        )
      );

      setViewNote({
        ...viewNote,
        content: editContent.trim(),
        category_id: editCategoryId || undefined,
        category: updatedCategory ? {
          ...updatedCategory,
          name: updatedCategory.decryptedName,
        } : undefined,
      });

      setIsEditingNote(false);
      setEditCategoryId(undefined);
      toast.success('Nota atualizada com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar nota');
    }
  }

  async function handleProtectNote() {
    if (!protectNote || !passwordInput.trim()) {
      toast.error('Digite uma senha valida');
      return;
    }

    if (passwordInput !== confirmPasswordInput) {
      toast.error('As senhas nao coincidem');
      return;
    }

    const success = await NotesService.setNotePassword(protectNote.id, passwordInput);

    if (success) {
      toast.success('Nota protegida com sucesso!');
      setProtectNote(null);
      setPasswordInput('');
      setConfirmPasswordInput('');
      await loadNotes(true);
    } else {
      toast.error('Nao foi possivel proteger a nota');
    }
  }

  async function handleUnprotectNote() {
    if (!unprotectNote || !passwordInput.trim()) {
      toast.error('Digite a senha');
      return;
    }

    const isValid = await NotesService.verifyNotePassword(unprotectNote.id, passwordInput);

    if (!isValid) {
      toast.error('Senha incorreta');
      setPasswordInput('');
      return;
    }

    const success = await NotesService.setNotePassword(unprotectNote.id, null);

    if (success) {
      toast.success('Protecao removida com sucesso!');
      setUnprotectNote(null);
      setPasswordInput('');
      await loadNotes(true);
    } else {
      toast.error('Nao foi possivel remover a protecao');
    }
  }

  async function handleVerifyPassword() {
    if (!verifyPasswordNote || !passwordInput.trim()) {
      toast.error('Digite a senha');
      return;
    }

    const isValid = await NotesService.verifyNotePassword(verifyPasswordNote.id, passwordInput);

    if (isValid) {
      setVerifyPasswordNote(null);
      setPasswordInput('');
      setViewNote(verifyPasswordNote);
    } else {
      toast.error('Senha incorreta');
      setPasswordInput('');
    }
  }

  function handleViewNote(note: DecryptedNote) {
    if (note.isProtected) {
      setVerifyPasswordNote(note);
    } else {
      setViewNote(note);
    }
  }

  function getWeekLabel(date: Date): string {
    const now = new Date();
    const noteDate = new Date(date);

    const startOfThisWeek = new Date(now);
    startOfThisWeek.setHours(0, 0, 0, 0);
    startOfThisWeek.setDate(now.getDate() - now.getDay());

    const startOfLastWeek = new Date(startOfThisWeek);
    startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);

    if (noteDate >= startOfThisWeek) {
      return 'Esta Semana';
    } else if (noteDate >= startOfLastWeek) {
      return 'Semana Passada';
    } else {
      const monthNames = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ];
      return `${monthNames[noteDate.getMonth()]} ${noteDate.getFullYear()}`;
    }
  }

  function groupNotesByWeek(notes: DecryptedNote[]): GroupedNotes {
    const grouped: GroupedNotes = {};

    notes.forEach((note) => {
      const weekLabel = getWeekLabel(new Date(note.created_at));
      if (!grouped[weekLabel]) {
        grouped[weekLabel] = [];
      }
      grouped[weekLabel].push(note);
    });

    return grouped;
  }

  const filteredNotes = notes.filter((note) => {
    const matchesSearch = searchQuery
      ? note.content.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    let matchesCategory = true;
    if (selectedCategory === undefined) {
      matchesCategory = true;
    } else if (selectedCategory === null) {
      matchesCategory = !note.category_id;
    } else {
      matchesCategory = note.category_id === selectedCategory;
    }

    return matchesSearch && matchesCategory;
  });

  const groupedNotes = groupNotesByWeek(filteredNotes);

  return (
    <div className="notes-container">
      <div className="notes-header">
        <h1 className="notes-title">Minhas Notas</h1>
        <p className="notes-count">{filteredNotes.length} nota(s)</p>
      </div>

      <div className="category-filters">
        <button
          className={`filter-chip ${selectedCategory === undefined ? 'active' : ''}`}
          onClick={() => setSelectedCategory(undefined)}
        >
          Todas
        </button>
        <button
          className={`filter-chip ${selectedCategory === null ? 'active' : ''}`}
          onClick={() => setSelectedCategory(null)}
        >
          Sem Categoria
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            className={`filter-chip ${selectedCategory === cat.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.id)}
            style={{ borderColor: cat.color }}
          >
            {cat.decryptedName}
          </button>
        ))}
      </div>

      <div className="search-container">
        <Search size={20} />
        <input
          type="text"
          placeholder="Pesquisar notas..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="search-clear">
            <X size={20} />
          </button>
        )}
      </div>

      <div className="notes-list">
        {loading ? (
          <p className="empty-text">Carregando...</p>
        ) : filteredNotes.length === 0 ? (
          <div className="empty-state">
            <p className="empty-text">
              {searchQuery ? 'Nenhuma nota encontrada' : 'Nenhuma nota ainda'}
            </p>
            <p className="empty-subtext">
              {searchQuery ? 'Tente outra pesquisa' : 'Crie sua primeira nota na tela inicial'}
            </p>
          </div>
        ) : (
          <>
            {Object.entries(groupedNotes).map(([weekLabel, weekNotes]) => {
              const isExpanded = expandedGroups.has(weekLabel);
              return (
                <div key={weekLabel} className="week-group">
                  <div
                    className="week-header"
                    onClick={() => toggleGroup(weekLabel)}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 0'
                    }}
                  >
                    <h2 className="week-label" style={{ margin: 0 }}>
                      {weekLabel} ({weekNotes.length})
                    </h2>
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                  {isExpanded && weekNotes.map((note) => (
                <div key={note.id} className="note-card">
                  <div onClick={() => handleViewNote(note)} style={{ cursor: 'pointer' }}>
                    {note.isProtected && (
                      <div className="protected-badge">
                        <Lock size={14} />
                        <span>Protegida</span>
                      </div>
                    )}
                    {note.category && (
                      <div className="note-category" style={{ borderColor: note.category.color }}>
                        {note.category.name}
                      </div>
                    )}
                    {note.photo_url && !note.isProtected && (
                      <div style={{ position: 'relative' }}>
                        <EncryptedImage src={note.photo_url} alt="" className="note-image" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(note.photo_url!, `nota-foto-${note.id}.jpg`);
                          }}
                          className="download-button"
                          title="Fazer download"
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Download size={20} />
                        </button>
                      </div>
                    )}
                    {note.video_url && !note.isProtected && (
                      <div style={{ position: 'relative' }}>
                        <EncryptedVideo src={note.video_url} controls className="note-image" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(note.video_url!, `nota-video-${note.id}.mp4`);
                          }}
                          className="download-button"
                          title="Fazer download"
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Download size={20} />
                        </button>
                      </div>
                    )}
                    {note.file_url && note.file_name && !note.isProtected && (
                      <FileAttachment
                        fileName={note.file_name}
                        fileType={note.file_type || 'application/octet-stream'}
                        fileUrl={note.file_url}
                        onDownload={() => handleDownload(note.file_url!, note.file_name!)}
                      />
                    )}
                    <p className="note-content">
                      {note.isProtected ? 'Conteudo protegido por senha' : note.content}
                    </p>
                  </div>
                  <div className="note-footer">
                    <div className="note-info">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span className="note-date">
                          {new Date(note.created_at).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        {!note.isProtected && note.file_size && note.file_size > 0 && (
                          <span style={{ fontSize: '0.75rem', color: '#666' }}>
                            {formatFileSize(note.file_size)}
                          </span>
                        )}
                      </div>
                      {note.latitude && note.longitude && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setMapNote(note);
                          }}
                          className="map-button"
                          title="Ver localização"
                        >
                          <MapPin size={16} />
                        </button>
                      )}
                    </div>
                    <div className="note-actions">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyContent(note.content);
                        }}
                        className="copy-button"
                        title="Copiar conteúdo"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#6c757d',
                          cursor: 'pointer',
                          padding: '8px',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f0f0f0';
                          e.currentTarget.style.color = '#495057';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#6c757d';
                        }}
                      >
                        <Copy size={20} />
                      </button>
                      {note.isProtected ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setUnprotectNote(note);
                          }}
                          className="protect-button protected"
                          title="Remover proteção"
                        >
                          <Unlock size={20} />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setProtectNote(note);
                          }}
                          className="protect-button"
                          title="Proteger nota"
                        >
                          <Lock size={20} />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(note.id);
                        }}
                        className="delete-button"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
                </div>
              );
            })}
            {hasMore && !loading && (
              <div
                ref={loadMoreRef}
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#666'
                }}
              >
                {loadingMore ? 'Carregando mais notas...' : ''}
              </div>
            )}
          </>
        )}
      </div>

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Excluir nota?</h2>
            <p className="modal-message">
              Esta acao nao pode ser desfeita. A nota sera permanentemente removida.
            </p>
            <div className="modal-actions">
              <button onClick={() => setDeleteConfirm(null)} className="modal-cancel">
                Cancelar
              </button>
              <button onClick={() => handleDelete(deleteConfirm)} className="modal-confirm">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {viewNote && (
        <div className="modal-overlay" onClick={() => {
          setViewNote(null);
          setIsEditingNote(false);
          setEditContent('');
          setEditCategoryId(undefined);
        }}>
          <div className="modal-content view-note-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{isEditingNote ? 'Editar Nota' : 'Nota Completa'}</h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {!isEditingNote && !viewNote.isProtected && (
                  <button
                    onClick={() => handleStartEdit(viewNote)}
                    className="edit-note-button"
                    title="Editar nota"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#007bff',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 123, 255, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                )}
                <button onClick={() => {
                  setViewNote(null);
                  setIsEditingNote(false);
                  setEditContent('');
                  setEditCategoryId(undefined);
                }} className="modal-close">
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="view-note-content">
              {viewNote.category && (
                <div className="note-category" style={{ borderColor: viewNote.category.color }}>
                  {viewNote.category.name}
                </div>
              )}
              {viewNote.photo_url && (
                <div style={{ position: 'relative' }}>
                  <EncryptedImage src={viewNote.photo_url} alt="" className="view-note-image" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(viewNote.photo_url!, `nota-foto-${viewNote.id}.jpg`);
                    }}
                    className="download-button"
                    title="Fazer download"
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Download size={20} />
                  </button>
                </div>
              )}
              {viewNote.video_url && (
                <div style={{ position: 'relative' }}>
                  <EncryptedVideo src={viewNote.video_url} controls className="view-note-image" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(viewNote.video_url!, `nota-video-${viewNote.id}.mp4`);
                    }}
                    className="download-button"
                    title="Fazer download"
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Download size={20} />
                  </button>
                </div>
              )}
              {viewNote.file_url && viewNote.file_name && (
                <FileAttachment
                  fileName={viewNote.file_name}
                  fileType={viewNote.file_type || 'application/octet-stream'}
                  fileUrl={viewNote.file_url}
                  onDownload={() => handleDownload(viewNote.file_url!, viewNote.file_name!)}
                />
              )}
              {isEditingNote ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                      Categoria
                    </label>
                    <select
                      value={editCategoryId === null ? '' : (editCategoryId || '')}
                      onChange={(e) => setEditCategoryId(e.target.value || null)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Sem Categoria</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.decryptedName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                      Conteudo
                    </label>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="edit-textarea"
                      rows={10}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        minHeight: '250px'
                      }}
                      placeholder="Digite o conteudo..."
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                    <button
                      onClick={handleCancelEdit}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        background: 'white',
                        color: '#666',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '8px',
                        background: '#007bff',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Salvar
                    </button>
                  </div>
                </div>
              ) : (
                <p className="view-note-text">{viewNote.content}</p>
              )}
              <div className="view-note-meta">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span className="note-date">
                    {new Date(viewNote.created_at).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {viewNote.file_size && viewNote.file_size > 0 && (
                    <span style={{ fontSize: '0.75rem', color: '#666' }}>
                      {formatFileSize(viewNote.file_size)}
                    </span>
                  )}
                </div>
                {viewNote.latitude && viewNote.longitude && (
                  <button
                    onClick={() => {
                      setMapNote(viewNote);
                      setViewNote(null);
                    }}
                    className="view-note-map-button"
                  >
                    <MapPin size={16} />
                    <span>Ver Localização</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {mapNote && mapNote.latitude && mapNote.longitude && (
        <div className="modal-overlay" onClick={() => setMapNote(null)}>
          <div className="modal-content map-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Localização da Nota</h2>
              <button onClick={() => setMapNote(null)} className="modal-close">
                <X size={24} />
              </button>
            </div>
            <div className="map-container">
              <iframe
                width="100%"
                height="400"
                frameBorder="0"
                style={{ border: 0, borderRadius: '12px' }}
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapNote.longitude - 0.01},${mapNote.latitude - 0.01},${mapNote.longitude + 0.01},${mapNote.latitude + 0.01}&layer=mapnik&marker=${mapNote.latitude},${mapNote.longitude}`}
                title="Mapa"
              />
              <div className="map-coords">
                <p>Lat: {mapNote.latitude.toFixed(6)}</p>
                <p>Lng: {mapNote.longitude.toFixed(6)}</p>
              </div>
              <a
                href={`https://www.google.com/maps?q=${mapNote.latitude},${mapNote.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="map-link"
              >
                Abrir no Google Maps
              </a>
            </div>
          </div>
        </div>
      )}

      {protectNote && (
        <div className="modal-overlay" onClick={() => {
          setProtectNote(null);
          setPasswordInput('');
          setConfirmPasswordInput('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Proteger Nota</h2>
            <p className="modal-message">
              Defina uma senha para proteger esta nota. Você precisara desta senha para visualizar a nota.
            </p>
            <input
              type="password"
              placeholder="Digite a senha"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              className="password-input"
              autoFocus
            />
            <input
              type="password"
              placeholder="Confirme a senha"
              value={confirmPasswordInput}
              onChange={(e) => setConfirmPasswordInput(e.target.value)}
              className="password-input"
              onKeyDown={(e) => e.key === 'Enter' && handleProtectNote()}
            />
            <div className="modal-actions">
              <button onClick={() => {
                setProtectNote(null);
                setPasswordInput('');
                setConfirmPasswordInput('');
              }} className="modal-cancel">
                Cancelar
              </button>
              <button onClick={handleProtectNote} className="modal-confirm protect">
                Proteger
              </button>
            </div>
          </div>
        </div>
      )}

      {verifyPasswordNote && (
        <div className="modal-overlay" onClick={() => {
          setVerifyPasswordNote(null);
          setPasswordInput('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Nota Protegida</h2>
              <button onClick={() => {
                setVerifyPasswordNote(null);
                setPasswordInput('');
              }} className="modal-close">
                <X size={24} />
              </button>
            </div>
            <p className="modal-message">
              Esta nota esta protegida por senha. Digite a senha para visualizar.
            </p>
            <input
              type="password"
              placeholder="Digite a senha"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              className="password-input"
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyPassword()}
              autoFocus
            />
            <div className="modal-actions">
              <button onClick={() => {
                setVerifyPasswordNote(null);
                setPasswordInput('');
              }} className="modal-cancel">
                Cancelar
              </button>
              <button onClick={handleVerifyPassword} className="modal-confirm">
                Verificar
              </button>
            </div>
          </div>
        </div>
      )}

      {unprotectNote && (
        <div className="modal-overlay" onClick={() => {
          setUnprotectNote(null);
          setPasswordInput('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Remover Protecao</h2>
              <button onClick={() => {
                setUnprotectNote(null);
                setPasswordInput('');
              }} className="modal-close">
                <X size={24} />
              </button>
            </div>
            <p className="modal-message">
              Digite a senha para remover a protecao desta nota.
            </p>
            <input
              type="password"
              placeholder="Digite a senha"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              className="password-input"
              onKeyDown={(e) => e.key === 'Enter' && handleUnprotectNote()}
              autoFocus
            />
            <div className="modal-actions">
              <button onClick={() => {
                setUnprotectNote(null);
                setPasswordInput('');
              }} className="modal-cancel">
                Cancelar
              </button>
              <button onClick={handleUnprotectNote} className="modal-confirm">
                Remover Protecao
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
